import '@fontsource/orbitron/600.css';
import '@fontsource/orbitron/800.css';
import '@fontsource/orbitron/900.css';
import '@fontsource/rajdhani/500.css';
import '@fontsource/rajdhani/700.css';
import './styles.css';

import {
  createSim,
  stepSim,
  chooseUpgrade,
  summarize,
  botInput,
  botChoice,
  InputRecorder,
  DT,
  MAX_TICKS,
  FINAL_TICK,
  SHIPS,
  WEAPONS,
  PASSIVES,
  shipYield,
  riftMods,
  RIFT_MAX,
  reviveSim,
  equippedParts
} from '../sim/index.js';
import { createRenderer, QUALITY_LEVELS } from './renderer.js';
import { createAudio } from './audio.js';
import { createInput } from './input.js';
import { createApi } from './api.js';
import { createWallet, explainError } from './wallet.js';
import { injected } from './injected.js';
import { iconCopy, drawShipPreview } from './sprites.js';
import { createPanels } from './panels.js';
import { founderRank, currentSkin, founderBusy } from './founder.js';
import { setupPwa } from './pwa.js';
import { PORTAL, initPortal, portal } from './portal.js';
import { chainConfigFromDeployment } from '../shared/networks.js';
import { $, el, toast, fmtTime, fmtNum, shortAddr, fmtRift, brandText } from './dom.js';
import { t, tx, lang, LANGS, setLang, applyStatic } from './i18n.js';
import {
  loadProgress,
  saveProgress,
  recordLocalRun,
  upgradeLocalTalent,
  localMissions,
  canUpgradeAny,
  recordChallenge,
  equipLocalPart,
  buyLocalCrate,
  mergeProgress,
  ownerBoost
} from './progress.js';
import { receiveTransfer, metamaskLink, applyTransfer } from './transfer.js';
import { moveIfOldHost, receiveMove } from '../rift/move.js';
import { applyCargoTransfer } from '../cargo/style.js';
import { start as startAccount, createSync, onAccount, account as rgAccount, setName as setAccountName, syncPurchases, loginWallet, reloadForAccount, isOnline as isAccountOnline } from '../rift/account.js';
import { createAccountUI } from '../rift/account-ui.js';
import { configureFiat, loadFiat } from '../rift/fiat-ui.js';
import { openInMetaMask, isTouch } from '../rift/open-in-metamask.js';
import { remoteWallet, remoteAddress } from '../rift/wallet.js';
import { dailyNumber, dailySeed, DAILY_RULES, msToNextDaily } from '../shared/daily.js';
import { DUEL_RULES, decodeDuel, duelUrl, cleanName } from '../shared/duel.js';
import { WORLD, fetchWorldDaily, submitWorldDaily, myPublicId, defaultName, fetchRunBoard, submitRunBoard } from './world.js';
import { shareResult } from './share.js';
import { TO_RIFTFALL, pending as bridgePending, nextStep as bridgeNext, cargoState } from '../rift/bridge.js';

applyStatic();

const canvas = $('#game');
const renderer = createRenderer(canvas);
const audio = createAudio();
const input = createInput({ surface: canvas, joystick: $('#joystick'), shipScreen: () => [renderer.R.shipSX, renderer.R.shipSY] });
const api = createApi();

const app = {
  api,
  audio,
  config: null,
  profile: null,
  wallet: null,
  balances: null,
  online: false,
  progress: loadProgress(),
  ship: loadShipChoice(),
  startRun,
  refreshProfile,
  connectWallet,
  selectShip,
  updateMenu,
  pilot,
  upgradeTalent,
  applyCosmetics,
  equipPart,
  buyCrate,
  skin: () => renderer.R.skin
};
const panels = createPanels(app);

// --------------------------------------------------------------------- estado de juego

const game = {
  mode: 'demo', // demo | play | over
  sim: null,
  rec: null,
  run: null,
  acc: 0,
  paused: false,
  choiceOpen: false,
  autopilot: false,
  offline: false,
  hudSig: ''
};

function newDemo() {
  const keys = Object.keys(SHIPS);
  const ship = keys[Math.floor(Math.random() * keys.length)];
  game.sim = createSim({ seed: (Math.random() * 2 ** 32) >>> 0, ship, shipLevel: 5 });
  // Arranca la demo con algo de acción ya en pantalla.
  for (let i = 0; i < 60 * 25; i++) {
    if (game.sim.phase === 'choice') chooseUpgrade(game.sim, botChoice(game.sim));
    else stepSim(game.sim, botInput(game.sim));
    game.sim.events.length = 0;
  }
  renderer.reset();
}

function loadShipChoice() {
  try {
    const v = JSON.parse(localStorage.getItem('riftfall.ship') ?? 'null');
    if (v && SHIPS[v.key]) return v;
  } catch {
    /* ignorar */
  }
  return { key: 'spark', tokenId: null, level: 1 };
}

/** Progreso del piloto: el del servidor si está en línea, si no el guardado en este dispositivo. */
function pilot() {
  if (app.online && app.profile) {
    const p = app.profile;
    return {
      online: true,
      cores: p.cores ?? 0,
      talents: p.talents ?? {},
      missions: p.missions ?? [],
      streak: p.streak ?? 0,
      riftMax: p.riftMax ?? 0,
      parts: p.parts ?? {},
      loadout: p.loadout ?? {},
      stats: p
    };
  }
  const p = app.progress;
  return {
    online: false,
    cores: p.cores,
    talents: p.talents,
    missions: localMissions(p),
    streak: p.streak,
    riftMax: p.riftMax ?? 0,
    parts: p.parts ?? {},
    loadout: p.loadout ?? {},
    stats: p
  };
}

// --------------------------------------------------------------------- Pase Fundador

/** Aplica los cosméticos del Pase Fundador (pintura, estela e insignia). */
function applyCosmetics() {
  const rank = founderRank();
  renderer.R.skin = currentSkin();
  renderer.R.trailColor = rank >= 1 ? '#ffd23d' : null;
  $('#founderChip').classList.toggle('hidden', rank === 0);
  $('#founderBanner').classList.toggle('owned', rank >= 3);
}

// --------------------------------------------------------------------- Desafío del Día

function updateDailyCard() {
  const n = dailyNumber();
  $('#dcNum').textContent = `#${n}`;
  const ms = msToNextDaily();
  $('#dcNext').textContent = t('dc.next', { h: Math.floor(ms / 3_600_000), m: Math.floor(ms / 60_000) % 60 });
  const ch = app.progress.challenge;
  const info = $('#dcInfo');
  if (ch?.n === n && ch.best) {
    info.replaceChildren(
      el('b', {}, t('dc.best', { score: fmtNum(ch.best.score), time: fmtTime(ch.best.timeSec) })),
      ' · ',
      t('dc.tries', { n: ch.tries })
    );
    $('#dcPlay').textContent = t('dc.again');
  } else {
    info.textContent = t('dc.rules');
    $('#dcPlay').textContent = t('dc.play');
  }
}
$('#dcPlay').addEventListener('click', () => startRun('daily'));

// --------------------------------------------------------------------- Ranking mundial del desafío

const WORLD_MAX = 50;
let worldBoard = null;
let worldMe = null;

function renderWorld() {
  const box = $('#dcWorld');
  const ok = WORLD && worldBoard && worldBoard.n === dailyNumber();
  box.classList.toggle('hidden', !ok);
  if (!ok) return;
  $('#wdRename').textContent = t('wd.as', { name: pilotName() || defaultName() });
  const entries = worldBoard.entries;
  const mine = entries.findIndex((e) => e.id === worldMe);
  const shown = entries.slice(0, 5);
  $('#wdList').replaceChildren(
    ...shown.map((e, i) =>
      el('li', { class: entries.indexOf(e) === mine ? 'me' : '' }, [el('span', {}, `#${i + 1}`), el('b', {}, e.name), el('em', {}, fmtNum(e.score))])
    )
  );
  const you = $('#wdYou');
  if (!entries.length) you.textContent = t('wd.none');
  else if (mine >= 0) you.innerHTML = t('wd.you', { n: mine + 1 });
  else if (entries.length >= WORLD_MAX) you.innerHTML = t('wd.out', { max: WORLD_MAX, score: fmtNum(entries[entries.length - 1].score) });
  else you.textContent = '';
}

async function refreshWorld() {
  if (!WORLD || app.online) return;
  try {
    worldMe ??= await myPublicId();
    worldBoard = await fetchWorldDaily(dailyNumber());
  } catch {
    worldBoard = null;
  }
  renderWorld();
}

function renamePilot() {
  const v = prompt(t('wd.prompt'), pilotName());
  if (v === null) return;
  try {
    localStorage.setItem(NAME_KEY, cleanName(v));
  } catch {
    /* sin almacenamiento */
  }
  // El nombre es de la Cuenta Rift: se ve igual en los dos juegos y en todos los dispositivos.
  setAccountName(cleanName(v)).catch(() => {});
  renderWorld();
  renderRunBoard();
}
$('#wdRename').addEventListener('click', renamePilot);
$('#rkRename').addEventListener('click', renamePilot);

// --------------------------------------------------------------------- Ranking de hoy (todas las partidas)

let runBoard = null;

function renderRunBoard() {
  const card = $('#rankCard');
  const ok = WORLD && !app.online && runBoard;
  card.classList.toggle('hidden', !ok);
  if (!ok) return;
  $('#rkRename').textContent = t('wd.as', { name: pilotName() || defaultName() });
  const entries = runBoard.today;
  const mine = entries.findIndex((e) => e.id === worldMe);
  $('#rkList').replaceChildren(
    ...entries.slice(0, 5).map((e, i) =>
      el('li', { class: i === mine ? 'me' : '' }, [el('span', {}, `#${i + 1}`), el('b', {}, e.name), el('em', {}, fmtNum(e.score))])
    )
  );
  const you = $('#rkYou');
  if (!entries.length) you.textContent = t('rk.none');
  else if (mine >= 0) you.innerHTML = t('wd.you', { n: mine + 1 });
  else you.textContent = t('rk.play');
}

async function refreshRunBoard() {
  if (!WORLD || app.online) return;
  try {
    worldMe ??= await myPublicId();
    runBoard = await fetchRunBoard();
  } catch {
    runBoard = null;
  }
  renderRunBoard();
}

/** Mejor puntaje de hoy ya enviado (para no mandar partidas que no cambian nada). */
const RANK_SENT = 'riftfall.rankSent';
function sentToday() {
  try {
    const v = JSON.parse(localStorage.getItem(RANK_SENT) ?? 'null');
    return v?.day === dailyNumber() ? v : null;
  } catch {
    return null;
  }
}

/** Fin de una partida normal: si es tu mejor de hoy va al ranking compartido (el servidor la re-juega). */
function submitRunToWorld(local, rec) {
  if (!WORLD || app.online || !(local.score > 0)) return;
  const sent = sentToday();
  const nameChanged = sent && sent.name !== pilotName();
  if (sent && local.score <= sent.best && !nameChanged) return;
  const row = el('li', { class: 'world-row' }, [el('span', {}, t('rk.checking')), el('b', {}, '')]);
  $('#goRewardList').append(row);
  submitRunBoard({ name: pilotName(), run: game.run, rec, summary: local })
    .then((res) => {
      try {
        localStorage.setItem(RANK_SENT, JSON.stringify({ day: dailyNumber(), best: Math.max(res.score, sent?.best ?? 0), name: pilotName() }));
      } catch {
        /* sin almacenamiento */
      }
      runBoard = res.board;
      row.firstChild.textContent = res.rankToday ? t('rk.rank', { n: res.rankToday }) : t('rk.out', { max: 50 });
      if (res.rankAll && res.rankAll <= 10) row.firstChild.textContent += ` · ${t('rk.allRank', { n: res.rankAll })}`;
      if (res.rankToday && res.rankToday <= 3) audio.play('levelup');
      renderRunBoard();
    })
    .catch(() => {
      row.firstChild.textContent = t('rk.sendErr');
    });
}

/** Fin de un desafío: la partida va al ranking mundial y la fila del resultado se actualiza sola. */
function submitDailyToWorld(n, rec, summary) {
  if (!WORLD || app.online) return;
  const row = el('li', { class: 'world-row' }, [el('span', {}, t('wd.checking')), el('b', {}, '')]);
  $('#goRewardList').append(row);
  submitWorldDaily({ n, name: pilotName(), rec, summary })
    .then((res) => {
      worldBoard = res.board;
      const text = res.rank
        ? res.best > res.score
          ? t('wd.kept', { n: res.rank })
          : t('wd.rank', { n: res.rank })
        : t('wd.outRow', { max: WORLD_MAX, score: fmtNum(res.cutoff) });
      row.firstChild.textContent = text;
      if (res.rank && res.best <= res.score) audio.play('levelup');
      renderWorld();
    })
    .catch(() => {
      row.firstChild.textContent = t('wd.err');
    });
}

// --------------------------------------------------------------------- Duelo con amigos

/** Reto recibido por link (?duel=…). */
let incomingDuel = null;
/** Duelo en juego: la semilla y el rival (null si es un reto nuevo). */
let duelCtx = null;
const NAME_KEY = 'riftfall.name';

function readIncomingDuel() {
  const code = new URLSearchParams(location.search).get('duel');
  if (!code || PORTAL) return;
  incomingDuel = decodeDuel(code);
  if (!incomingDuel) toast(t('du.bad'), 'err');
}

function updateDuelInvite() {
  $('#duelInvite').classList.toggle('hidden', !incomingDuel);
  if (!incomingDuel) return;
  const d = incomingDuel;
  $('#duelFrom').textContent = d.name ? t('du.from', { name: d.name }) : t('du.fromAnon');
  $('#duelMark').textContent = t('du.mark', { score: fmtNum(d.score), time: fmtTime(d.timeSec), kills: fmtNum(d.kills) });
}

function pilotName() {
  try {
    return cleanName(localStorage.getItem(NAME_KEY));
  } catch {
    return '';
  }
}

$('#duelAccept').addEventListener('click', () => {
  duelCtx = { seed: incomingDuel.seed, opp: incomingDuel };
  startRun('duel');
});
$('#duelPlay').addEventListener('click', () => {
  duelCtx = { seed: (Math.random() * 2 ** 32) >>> 0, opp: null };
  startRun('duel');
});

/** Fin de un duelo: quién ganó y el botón para mandar (o devolver) el reto. */
function showDuelResult(duel, local) {
  $('#goDuel').classList.toggle('hidden', !duel);
  $('#againBtn').textContent = duel ? t('du.again') : t('go.again');
  if (!duel) return;
  const opp = duel.opp;
  const name = opp?.name || t('du.friend');
  const diff = opp ? local.score - opp.score : 0;
  const res = $('#goDuelResult');
  res.className = !opp ? '' : diff > 0 ? 'won' : diff < 0 ? 'lost' : '';
  res.textContent = !opp
    ? t('du.ready')
    : diff > 0
      ? t('du.won', { name, diff: fmtNum(diff) })
      : diff < 0
        ? t('du.lost', { name, diff: fmtNum(-diff) })
        : t('du.tie', { name });
  $('#duelSend').textContent = opp ? t('du.sendBack') : t('du.send');
  $('#duelName').value = pilotName();
}

$('#duelSend').addEventListener('click', async () => {
  if (lastResult?.duelSeed == null) return;
  const name = cleanName($('#duelName').value);
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    /* sin almacenamiento */
  }
  const s = lastResult.summary;
  const url = duelUrl(location.origin, { seed: lastResult.duelSeed, score: s.score, timeSec: s.timeSec, kills: s.kills, victory: s.victory, name });
  const b = $('#duelSend');
  b.disabled = true;
  try {
    await shareResult({ ...lastResult, duelUrl: url });
  } finally {
    b.disabled = false;
  }
});

// --------------------------------------------------------------------- Nivel del Rift

const RIFT_KEY = 'riftfall.rift';
let riftChoice = 0;
function readRiftChoice() {
  try {
    riftChoice = Math.max(0, Math.floor(Number(localStorage.getItem(RIFT_KEY)) || 0));
  } catch {
    riftChoice = 0;
  }
}
readRiftChoice();

/** Nivel elegido, nunca por encima del máximo desbloqueado. */
function riftLevel() {
  return Math.min(riftChoice, pilot().riftMax);
}

function setRift(n) {
  riftChoice = Math.max(0, Math.min(pilot().riftMax, n));
  try {
    localStorage.setItem(RIFT_KEY, String(riftChoice));
  } catch {
    /* sin almacenamiento */
  }
  updateRiftPick();
}

function updateRiftPick() {
  const max = pilot().riftMax;
  const lv = riftLevel();
  const m = riftMods(lv);
  const box = $('#riftPick');
  box.style.setProperty('--heat', String(Math.round(190 - (lv / RIFT_MAX) * 190)));
  $('#riftNum').textContent = String(lv);
  $('#riftPips').replaceChildren(
    ...Array.from({ length: RIFT_MAX }, (_, i) => el('i', { class: i < lv ? 'on' : i < max ? 'open' : '' }))
  );
  const desc = $('#riftDesc');
  if (lv === 0) desc.replaceChildren(t('rift.normal'), ' · ', el('em', {}, t('rift.reward', { r: '1.00' })));
  else {
    desc.replaceChildren(
      t('rift.mods', { hp: Math.round((m.hp - 1) * 100), dmg: Math.round((m.dmg - 1) * 100) }),
      ' · ',
      el('em', {}, t('rift.reward', { r: m.reward.toFixed(2) }))
    );
  }
  $('#riftPrev').disabled = lv === 0;
  const next = $('#riftNext');
  const locked = lv >= max;
  next.disabled = lv >= RIFT_MAX;
  next.classList.toggle('locked', locked && lv < RIFT_MAX);
  next.textContent = locked && lv < RIFT_MAX ? '🔒' : '›';
}

$('#riftPrev').addEventListener('click', () => {
  audio.play('click');
  setRift(riftLevel() - 1);
});
$('#riftNext').addEventListener('click', () => {
  const lv = riftLevel();
  if (lv >= pilot().riftMax) {
    toast(t('rift.locked', { n: lv, m: lv + 1 }));
    return;
  }
  audio.play('click');
  setRift(lv + 1);
});

async function upgradeTalent(id) {
  if (app.online && app.profile) {
    app.profile = (await api.upgradeTalent(id)).profile;
    updateMenu();
    return app.profile.talents[id];
  }
  const lv = upgradeLocalTalent(app.progress, id);
  updateMenu();
  return lv;
}

/** Equipa o quita una pieza (con servidor o en este dispositivo). */
async function equipPart(slot, id) {
  if (app.online && app.profile) app.profile = (await api.equipPart(slot, id)).profile;
  else equipLocalPart(app.progress, slot, id);
  updateMenu();
}

/** Compra una caja de piezas con Núcleos. Devuelve lo obtenido. */
async function buyCrate() {
  let got;
  if (app.online && app.profile) {
    const res = await api.buyCrate();
    app.profile = res.profile;
    got = res.got;
  } else got = buyLocalCrate(app.progress);
  updateMenu();
  return got;
}

function selectShip(choice) {
  app.ship = choice;
  try {
    localStorage.setItem('riftfall.ship', JSON.stringify(choice));
  } catch {
    /* ignorar */
  }
  updateMenu();
}

// --------------------------------------------------------------------- menú

let previewT = 0;
function updateMenu() {
  const s = SHIPS[app.ship.key];
  $('#shipName').textContent = `${s.name}${app.ship.key !== 'spark' ? ` · ${t('hud.level', { n: app.ship.level })}` : ''}`;
  // Una nave NFT solo se puede volar con la wallet conectada.
  const needsWallet = !!app.ship.tokenId && !app.wallet?.connected;
  $('#shipDesc').textContent = needsWallet ? t('ship.needWallet') : tx.shipDesc(app.ship.key);
  $('#shipDesc').classList.toggle('warn', needsWallet);
  $('#shipYield').textContent = t('ship.yield', { m: shipYield(app.ship.key, app.ship.level).toFixed(2) });
  const p = app.profile;
  const pl = pilot();
  $('#menuCores').textContent = fmtNum(pl.cores);
  $('#shardChip').classList.toggle('hidden', !pl.online);
  $('#menuShards').textContent = fmtNum(p?.shards ?? 0);
  $('#talentDot').classList.toggle('hidden', !canUpgradeAny(pl.cores, pl.talents));
  if (app.balances) {
    $('#riftChip').classList.remove('hidden');
    $('#menuRift').textContent = fmtRift(app.balances.rift);
  }
  const wb = $('#walletBtn');
  const walletAddr = p?.wallet ?? (app.wallet?.connected ? app.wallet.address : null) ?? (!injected() ? rgAccount()?.wallets?.[0] : null) ?? null;
  // Sin token lanzado la wallet no sirve para nada: mejor no mostrar el botón.
  wb.classList.toggle('hidden', !app.config?.chain && !walletAddr);
  if (walletAddr) {
    wb.textContent = shortAddr(walletAddr);
    wb.classList.add('connected');
  } else {
    wb.textContent = app.config?.chain ? t('menu.connect') : t('menu.walletNoNet');
  }
  $('#streakTag').textContent = t('menu.streak', { n: pl.streak });
  updateRiftPick();
  updateDailyCard();
  updateDuelInvite();
  renderWorld();
  renderRunBoard();
  const unit = pl.online ? '◆' : '✦';
  $('#missionList').replaceChildren(
    ...pl.missions.map((m) =>
      el('li', { class: m.done ? 'done' : '' }, [
        el('span', {}, `${m.done ? '✔ ' : ''}${tx.missionName(m.id, m.name)}`),
        el('b', {}, `+${m.reward} ${unit}`),
        el('div', { class: 'bar' }, [el('i', { style: `width:${Math.round((m.progress / m.goal) * 100)}%` })])
      ])
    )
  );
}

function setNet(text, cls) {
  const n = $('#netStatus');
  n.textContent = text;
  n.className = cls;
}

async function refreshProfile() {
  try {
    app.profile = await api.profile();
  } catch {
    /* sin servidor */
  }
  if (app.wallet?.connected) {
    try {
      app.balances = await app.wallet.balances();
    } catch {
      app.balances = null;
    }
  }
  updateMenu();
}

/** La primera vez que se conecta una wallet con saldo del token, se ofrece mostrarlo en la wallet. */
function offerWatchToken(address) {
  const key = `riftfall.watched.${app.config.chain.chainId}.${address.toLowerCase()}`;
  try {
    if (localStorage.getItem(key) || !(app.balances?.rift > 0n)) return;
    localStorage.setItem(key, '1');
  } catch {
    return;
  }
  app.wallet.watchToken().catch(() => {});
}

/**
 * Sin wallet en el navegador: una sola conexión de WalletConnect para todo. La primera vez la wallet se
 * suma a tu Cuenta Rift (firma gratis); el Hangar, el token y las compras usan esa misma conexión, que
 * queda guardada para la próxima vez.
 */
async function connectRemoteWallet() {
  const wb = $('#walletBtn');
  saveProgress(app.progress);
  wb.disabled = true;
  wb.textContent = t('menu.connecting');
  try {
    let address;
    try {
      address = await remoteAddress();
    } catch (err) {
      if (err?.code === 4001) return false; // canceló o rechazó en la wallet
      const e = new Error(err?.message ?? 'wc');
      e.code = 'mmconnect';
      throw e;
    }
    if (isAccountOnline() && !(rgAccount()?.wallets ?? []).includes(address)) {
      const r = await loginWallet();
      if (r.switched) {
        // Esa wallet ya era de otra cuenta tuya: se entra a esa y se recarga con su progreso.
        toast(t('toast.walletConnected', { a: shortAddr(address) }), 'ok');
        reloadForAccount();
        return true;
      }
      if (await syncPurchases()) applyCosmetics();
    }
    await app.wallet?.connect();
    toast(t('toast.walletConnected', { a: shortAddr(address) }), 'ok');
    updateMenu();
    app.balances = (await app.wallet?.balances().catch(() => null)) ?? null;
    return true;
  } catch (err) {
    if (err?.code === 'mmconnect') openInMetaMask(metamaskLink, { lang });
    else if (err?.code !== 'rejected' && err?.code !== 'cancelled') toast(explainError(err), 'err');
    return false;
  } finally {
    wb.disabled = false;
    updateMenu();
  }
}

/** Después de sumar la wallet desde la Cuenta Rift: el Hangar y el token usan la misma conexión. */
async function attachRemoteWallet() {
  if (!app.wallet || app.wallet.connected || injected() || !remoteWallet()) return;
  if (await app.wallet.reconnect().catch(() => null)) {
    app.balances = await app.wallet.balances().catch(() => null);
    updateMenu();
  }
}

async function connectWallet() {
  if (!app.config?.chain) {
    toast(t('toast.noChain'), 'err');
    return false;
  }
  // Sin wallet en el navegador: se conecta por WalletConnect (celular o QR en la compu) y la wallet
  // queda en tu Cuenta Rift. Sin WalletConnect o sin servidor de cuentas, en el celular queda abrir el
  // juego dentro de MetaMask.
  if (!injected() && remoteWallet()) return connectRemoteWallet();
  if (!injected() && isTouch()) {
    saveProgress(app.progress);
    openInMetaMask(metamaskLink, { lang });
    return false;
  }
  const wb = $('#walletBtn');
  wb.disabled = true;
  wb.textContent = t('menu.connecting');
  try {
    const address = await app.wallet.connect();
    if (app.config.staticMode) {
      toast(t('toast.walletConnected', { a: shortAddr(address) }), 'ok');
      updateMenu();
      // Los saldos se leen de la red: si tarda o falla, la wallet igual queda conectada.
      app.balances = await app.wallet.balances().catch(() => null);
      updateMenu();
      offerWatchToken(address);
      return true;
    }
    const { message } = await api.nonce(address);
    const signature = await app.wallet.signMessage(message);
    app.profile = await api.loginWallet(address, signature);
    app.balances = await app.wallet.balances();
    toast(t('toast.walletConnected', { a: shortAddr(address) }), 'ok');
    updateMenu();
    return true;
  } catch (err) {
    toast(explainError(err), 'err');
    return false;
  } finally {
    wb.disabled = false;
    updateMenu();
  }
}

// --------------------------------------------------------------------- partida

let practiceNoticeShown = false;
/** Redes de prueba: monedas y tokens sin valor real. */
const TESTNETS = [97, 84532, 31337];

/** Pausa el sonido mientras se ve un anuncio del portal. */
const adAudio = { onStart: () => audio.suspend(true), onEnd: () => audio.suspend(false) };
let runsThisSession = 0;

async function startRun(mode = 'normal') {
  audio.unlock();
  audio.startMusic();
  panels.close();
  // Portales: un anuncio en la pausa natural entre partidas (el portal limita la frecuencia).
  if (PORTAL && runsThisSession > 0) await portal.ad('midgame', adAudio);
  runsThisSession++;
  // Nave NFT elegida: hace falta la wallet conectada (y que la nave siga siendo suya) para volarla.
  if (mode === 'normal' && app.config?.staticMode && app.ship.tokenId) {
    if (!app.wallet?.connected && (!(injected() || remoteWallet()) || !(await connectWallet()))) toast(t('toast.sparkFallback', { name: SHIPS[app.ship.key].name }));
    else if (app.wallet?.connected) {
      const owned = await app.wallet.myShips().catch(() => null);
      const mine = owned?.find((s) => s.tokenId === app.ship.tokenId);
      if (owned && !mine) selectShip({ key: 'spark', tokenId: null, level: 1 });
      else if (mine && mine.level !== app.ship.level) selectShip({ ...app.ship, level: mine.level });
    }
  }
  let run = null;
  game.offline = false;
  try {
    if (PORTAL) throw new Error('sin servidor en portales');
    // El duelo usa la semilla del link: se juega en el dispositivo, sin recompensas del servidor.
    if (mode === 'duel') throw new Error('duelo sin servidor');
    run = await api.startRun({
      mode,
      rift: mode === 'normal' ? riftLevel() : 0,
      shipTokenId: app.ship.tokenId ?? undefined,
      demoShip: app.config?.demoShips ? app.ship.key : undefined
    });
  } catch (err) {
    // Con servidor en línea, un error de la API es un aviso real (p. ej. arena sin inscripción).
    // Sin servidor (hosting estático) se juega en modo práctica.
    if (err.status && app.online) {
      toast(err.message, 'err');
      return;
    }
    game.offline = true;
    // En práctica puedes volar tu nave NFT (no hay recompensas que verificar).
    const own = app.config?.staticMode && app.wallet?.connected && app.ship.tokenId ? app.ship : { key: 'spark', level: 1 };
    if (mode === 'daily') {
      const n = dailyNumber();
      run = { runId: null, seed: dailySeed(n), ship: DAILY_RULES.ship, shipLevel: DAILY_RULES.shipLevel, talents: {}, rift: DAILY_RULES.rift, mode: 'daily', daily: n };
    } else if (mode === 'duel') {
      run = { runId: null, seed: duelCtx.seed, ship: DUEL_RULES.ship, shipLevel: DUEL_RULES.shipLevel, talents: {}, rift: DUEL_RULES.rift, mode: 'duel', duel: duelCtx };
    } else {
      run = {
        runId: null,
        seed: (Math.random() * 2 ** 32) >>> 0,
        ship: own.key,
        shipLevel: own.level,
        talents: app.progress.talents,
        parts: equippedParts(app.progress.loadout, app.progress.parts),
        rift: riftLevel(),
        mode: 'normal'
      };
    }
    if (!practiceNoticeShown && !PORTAL && mode !== 'duel') {
      practiceNoticeShown = true;
      toast(t('toast.practice'));
    }
  }
  game.run = run;
  game.sim = createSim({ seed: run.seed, ship: run.ship, shipLevel: run.shipLevel, talents: run.talents, parts: run.parts, rift: run.rift ?? 0 });
  game.rec = new InputRecorder();
  game.acc = 0;
  game.mode = 'play';
  game.paused = false;
  game.choiceOpen = false;
  game.hudSig = '';
  renderer.reset();
  renderer.render(game.sim, 1, 0, { snap: true });
  $('#menu').classList.add('hidden');
  $('#gameover').classList.add('hidden');
  $('#hud').classList.remove('hidden');
  $('#bossBar').classList.add('hidden');
  const hudRift = $('#hudRift');
  hudRift.textContent = run.mode === 'daily' ? t('dc.hud', { n: run.daily }) : run.mode === 'duel' ? t('du.hud') : t('hud.rift', { n: game.sim.rift });
  hudRift.classList.toggle('hidden', game.sim.rift === 0 && !['daily', 'duel'].includes(run.mode));
  input.setEnabled(true);
  game.reviveOffered = false;
  portal.gameplayStart();
  const opp = run.duel?.opp;
  if (run.mode === 'daily') announce(t('ann.daily', { n: run.daily }), t('ann.dailySub'), 'good');
  else if (run.mode === 'duel') announce(t('du.hud'), opp ? t('du.vs', { name: opp.name || t('du.friend'), score: fmtNum(opp.score) }) : t('du.new'), 'good');
  else announce(run.mode === 'arena' ? t('ann.arena') : t('ann.survive'), run.mode === 'arena' ? t('ann.arenaSub') : t('ann.surviveSub'), 'good');
  showTutorial();
}

const TUTORIAL = [
  () => (matchMedia('(pointer: coarse)').matches ? t('tut.moveTouch') : t('tut.moveKeys')),
  () => t('tut.auto'),
  () => t('tut.gems'),
  () => t('tut.evo'),
  () => t('tut.final')
];
function showTutorial() {
  let seen = false;
  try {
    seen = localStorage.getItem('riftfall.tutorial') === '1';
    localStorage.setItem('riftfall.tutorial', '1');
  } catch {
    seen = false;
  }
  if (seen) return;
  const box = $('#tutorial');
  TUTORIAL.forEach((line, i) => {
    setTimeout(() => {
      if (game.mode !== 'play') return box.classList.add('hidden');
      box.innerHTML = line();
      box.classList.remove('hidden');
      box.style.animation = 'none';
      void box.offsetWidth;
      box.style.animation = '';
    }, 1800 + i * 4200);
  });
  setTimeout(() => box.classList.add('hidden'), 1800 + TUTORIAL.length * 4200);
}

function pickChoice(i) {
  const s = game.sim;
  if (!game.choiceOpen || s.phase !== 'choice' || !s.choice[i]) return;
  game.rec.choice(i);
  chooseUpgrade(s, i);
  audio.play('choose');
  game.choiceOpen = false;
  $('#levelup').classList.add('hidden');
  game.hudSig = '';
  if (s.phase === 'choice') openChoice();
}

function describeChoice(o) {
  if (o.kind === 'weapon') {
    return { title: tx.weaponName(o.id), text: tx.weaponDesc(o.id, o.level), color: WEAPONS[o.id].color, icon: o.id, max: 5 };
  }
  if (o.kind === 'passive') {
    return { title: tx.passiveName(o.id), text: tx.passiveDesc(o.id), color: '#ffc94d', icon: o.id, max: PASSIVES[o.id].max };
  }
  if (o.kind === 'evolve') {
    const text = t('choice.evoText', { desc: tx.evoDesc(o.id), weapon: tx.weaponName(o.id) });
    return { title: tx.evoName(o.id), text, color: '#ffd23d', icon: o.id, max: 0, evo: true };
  }
  if (o.kind === 'repair') return { title: t('choice.repair'), text: t('choice.repairText'), color: '#4dff9a', icon: 'repair', max: 0 };
  return { title: t('choice.cache'), text: t('choice.cacheText'), color: '#ffc94d', icon: 'cache', max: 0 };
}

function openChoice() {
  const s = game.sim;
  game.choiceOpen = true;
  const chest = s.choiceSource === 'chest';
  $('.levelup-inner').classList.toggle('chest', chest);
  $('#levelupKicker').textContent = chest ? t('lvl.chestKicker') : t('lvl.kicker');
  $('#levelupTitle').textContent = chest ? t('lvl.chestTitle') : t('lvl.title', { n: s.player.level - s.pendingLevels + 1 });
  const box = $('#choices');
  box.replaceChildren(
    ...s.choice.map((o, i) => {
      const d = describeChoice(o);
      const card = el('button', { class: `choice${d.evo ? ' evo' : ''}`, style: `--accent:${d.color}` }, [
        d.evo
          ? el('span', { class: 'tag evo' }, t('lvl.evo'))
          : o.level === 1 && o.kind !== 'repair' && o.kind !== 'cache'
          ? el('span', { class: 'tag new' }, t('lvl.new'))
          : o.level
            ? el('span', { class: 'tag' }, t('hud.level', { n: o.level }))
            : null,
        iconCopy(d.icon, d.color, 128),
        el('h4', {}, d.title),
        el('p', {}, d.text),
        d.max ? el('div', { class: 'pips' }, Array.from({ length: d.max }, (_, k) => el('i', { class: k < o.level ? 'on' : '' }))) : null,
        el('span', { class: 'key' }, `[${i + 1}]`)
      ]);
      card.querySelector('canvas').classList.add('ico');
      card.addEventListener('click', () => pickChoice(i));
      return card;
    })
  );
  $('#levelup').classList.remove('hidden');
}

/** Portales: al caer, se ofrece revivir una vez por partida a cambio de un anuncio. */
function offerRevive() {
  game.mode = 'revive';
  game.reviveOffered = true;
  input.setEnabled(false);
  portal.gameplayStop();
  const box = $('#revive');
  const bar = $('#reviveBar');
  box.classList.remove('hidden');
  bar.style.transition = 'none';
  bar.style.width = '100%';
  void bar.offsetWidth;
  bar.style.transition = 'width 8s linear';
  bar.style.width = '0%';
  let decided = false;
  const decline = () => {
    if (decided) return;
    decided = true;
    clearTimeout(timer);
    box.classList.add('hidden');
    endRun();
  };
  const timer = setTimeout(decline, 8000);
  $('#reviveNo').onclick = decline;
  $('#reviveAd').onclick = async () => {
    if (decided) return;
    decided = true;
    clearTimeout(timer);
    const ok = await portal.ad('rewarded', adAudio);
    box.classList.add('hidden');
    if (!ok || !reviveSim(game.sim)) {
      endRun();
      return;
    }
    game.mode = 'play';
    game.acc = 0;
    input.setEnabled(true);
    portal.gameplayStart();
  };
}

async function endRun() {
  game.mode = 'over';
  input.setEnabled(false);
  portal.gameplayStop();
  if (game.sim.phase === 'victory') portal.happytime();
  const s = game.sim;
  const local = summarize(s);
  const victory = s.phase === 'victory';
  audio.setIntensity(0.15);
  setTimeout(() => showGameOver(local, victory), victory ? 600 : 1300);
}

let lastResult = null;

/** Filas del fin de partida con las piezas que salieron de las cajas. */
function crateRows(crates = []) {
  return crates.map((c) => [
    `📦 ${c.refund ? t('pt.maxed', { part: tx.partName(c.id) }) : c.lv === 1 ? t('pt.new', { part: tx.partName(c.id) }) : t('pt.up', { part: tx.partName(c.id), n: c.lv })}`,
    c.refund ? `+${c.refund} ✦` : '',
    ''
  ]);
}

async function showGameOver(local, victory) {
  const card = $('.gameover-card');
  card.classList.toggle('victory', victory);
  card.classList.toggle('defeat', !victory);
  const retreat = !victory && game.sim.phase !== 'dead';
  const collapse = !victory && game.sim.tick >= MAX_TICKS;
  const title = victory ? t('go.titleVictory') : collapse ? t('go.titleCollapse') : retreat ? t('go.titleRetreat') : t('go.titleDead');
  $('#goKicker').textContent = victory ? t('go.victory') : t('go.kicker');
  $('#goTitle').textContent = title;
  const daily = game.run?.mode === 'daily' ? game.run.daily : null;
  const duel = game.run?.mode === 'duel' ? game.run.duel : null;
  lastResult = { summary: local, shipKey: game.sim.shipKey, title: daily ? t('dc.hud', { n: daily }) : duel ? t('du.hud') : title, daily, duelSeed: duel?.seed ?? null };
  showDuelResult(duel, local);
  // Desafío del Día: se guarda la mejor marca de hoy en este dispositivo (también con servidor).
  const challengeRow = () => {
    if (!daily) return [];
    const ch = recordChallenge(app.progress, daily, local);
    if (ch.newBest && ch.tries > 1) setTimeout(() => toast(t('dc.newBest', { n: daily }), 'ok'), 900);
    return [[t('dc.row', { n: daily }), fmtNum(ch.best.score), '']];
  };
  $('#goStats').replaceChildren(
    ...[
      [t('go.time'), fmtTime(local.timeSec)],
      [t('go.kills'), fmtNum(local.kills)],
      [t('go.combo'), `x${fmtNum(local.bestCombo ?? 0)}`],
      [t('go.score'), fmtNum(local.score)]
    ].map(([k, v]) => el('div', {}, [el('small', {}, k), el('b', {}, String(v))]))
  );
  const verify = $('#goVerify');
  const list = $('#goRewardList');
  list.replaceChildren();
  $('#goTotal').textContent = '…';
  $('#goUpgradeHint').classList.add('hidden');
  $('#hud').classList.add('hidden');
  $('#gameover').classList.remove('hidden');

  const showRows = (rows) =>
    rows.forEach(([label, v, unit = '◆'], i) => {
      const value = unit === '' ? v : `+${v} ${unit}`;
      list.append(el('li', { style: `animation-delay:${0.15 + i * 0.12}s` }, [el('span', {}, label), el('b', {}, value)]));
    });
  const afterRewards = () => {
    const pl = pilot();
    $('#goUpgradeHint').classList.toggle('hidden', !canUpgradeAny(pl.cores, pl.talents));
    updateMenu();
  };

  if (game.offline || !game.run?.runId) {
    // Práctica: el progreso (Núcleos, misiones y racha) se guarda en este dispositivo.
    verify.className = 'verify';
    verify.textContent = t('go.practice');
    $('#goTotalLabel').textContent = t('go.coresEarned');
    // El duelo sigue las reglas parejas del desafío: sin cajas ni desbloqueo de niveles.
    const res = recordLocalRun(app.progress, local, { daily: !!daily || !!duel });
    // Portales: duplicar los Núcleos de la partida viendo un anuncio (opcional, una vez).
    const dbl = $('#doubleBtn');
    dbl.classList.toggle('hidden', !(PORTAL && portal.ads && res.cores > 0));
    dbl.disabled = false;
    dbl.onclick = async () => {
      dbl.disabled = true;
      if (!(await portal.ad('rewarded', adAudio))) {
        dbl.disabled = false;
        return;
      }
      app.progress.cores += res.cores;
      app.progress.lifetimeCores = (app.progress.lifetimeCores ?? 0) + res.cores;
      saveProgress(app.progress);
      dbl.classList.add('hidden');
      toast(t('rv.doubled', { n: res.cores }), 'ok');
      countUp($('#goTotal'), res.total + res.cores);
      afterRewards();
    };
    const rows = [...challengeRow(), [t('go.rowCores'), res.cores, '✦']];
    const worldRec = daily && !PORTAL ? game.rec.finish() : null;
    const runRec = !daily && !duel && !PORTAL && game.run?.mode === 'normal' ? game.rec.finish() : null;
    if (res.streak) rows.push([t('go.rowStreak'), res.streak, '✦']);
    for (const m of res.missions) rows.push([t('go.rowMission', { name: tx.missionName(m.id, m.name) }), m.reward, '✦']);
    rows.push(...crateRows(res.crates));
    if (res.newBest && app.progress.runs > 1) rows.push([t('go.newBest'), fmtNum(local.score), '']);
    if (res.riftUnlocked) rows.push([`🔓 ${t('go.riftUnlocked', { n: res.riftUnlocked })}`, '', '']);
    showRows(rows);
    if (worldRec) submitDailyToWorld(daily, worldRec, local);
    if (runRec) submitRunToWorld(local, runRec);
    maybeSuggestFounder(local);
    countUp($('#goTotal'), res.total);
    if (res.total > 0) setTimeout(() => audio.play('shard'), 400);
    afterRewards();
    return;
  }
  $('#goTotalLabel').textContent = t('go.shardsEarned');
  verify.className = 'verify';
  verify.replaceChildren(el('span', { class: 'spinner' }), t('go.verifying'));
  try {
    const res = await api.finishRun({ runId: game.run.runId, ...game.rec.finish() });
    verify.className = 'verify ok';
    verify.textContent = t('go.verified');
    const rows = challengeRow();
    if (game.run.mode === 'arena') rows.push([t('go.rowArena'), fmtNum(res.summary.score), '']);
    else {
      if (!daily) rows.push([t('go.rowRun', { m: res.summary.yieldMult.toFixed(2) }), res.rewards.run]);
      if (res.rewards.streak) rows.push([t('go.rowStreak'), res.rewards.streak]);
      for (const m of res.rewards.missions) rows.push([t('go.rowMission', { name: tx.missionName(m.id, m.name) }), m.reward]);
    }
    if (res.cores) rows.push([t('go.rowCores'), res.cores, '✦']);
    rows.push(...crateRows(res.crates));
    if (res.riftUnlocked) rows.push([`🔓 ${t('go.riftUnlocked', { n: res.riftUnlocked })}`, '', '']);
    showRows(rows);
    countUp($('#goTotal'), res.totalShards);
    if (res.totalShards > 0) setTimeout(() => audio.play('shard'), 400);
    app.profile = res.profile;
    afterRewards();
  } catch (err) {
    verify.className = 'verify err';
    verify.textContent = `✖ ${err.message}`;
    $('#goTotal').textContent = '0';
  }
}

// Sugerencia del Pase Fundador: solo en la web, a quien ya jugó varias partidas y acaba de hacer una buena,
// como mucho una vez por día y nunca a quien ya es Fundador.
const UPSELL_KEY = 'riftfall.upsellDay';
function maybeSuggestFounder(local) {
  const box = $('#goFounder');
  box.classList.add('hidden');
  if (PORTAL || founderRank() > 0 || (app.progress.runs ?? 0) < 3 || local.timeSec < 120) return;
  const today = new Date().toISOString().slice(0, 10);
  try {
    if (localStorage.getItem(UPSELL_KEY) === today) return;
    localStorage.setItem(UPSELL_KEY, today);
  } catch {
    return;
  }
  box.classList.remove('hidden');
}
$('#goFounderBtn').addEventListener('click', () => {
  backToMenu();
  panels.open('founder');
});
$('#goFounderNo').addEventListener('click', () => $('#goFounder').classList.add('hidden'));

function countUp(node, target) {
  const t0 = performance.now();
  const dur = 900;
  const tick = (t) => {
    const k = Math.min(1, (t - t0) / dur);
    node.textContent = fmtNum(Math.round(target * (1 - Math.pow(1 - k, 3))));
    if (k < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function backToMenu() {
  game.mode = 'demo';
  $('#revive').classList.add('hidden');
  $('#gameover').classList.add('hidden');
  $('#hud').classList.add('hidden');
  $('#pause').classList.add('hidden');
  $('#levelup').classList.add('hidden');
  $('#menu').classList.remove('hidden');
  input.setEnabled(false);
  newDemo();
  refreshProfile();
}

function setPaused(v) {
  if (game.mode !== 'play') return;
  game.paused = v;
  if (v) portal.gameplayStop();
  else portal.gameplayStart();
  $('#pause').classList.toggle('hidden', !v);
  if (v) {
    const box = $('#pauseLoadout');
    box.replaceChildren(...loadoutSlots(game.sim));
  }
}

// --------------------------------------------------------------------- HUD

let announceTimer = null;
function announce(title, sub, cls = 'good') {
  const a = $('#announce');
  a.className = '';
  a.replaceChildren(document.createTextNode(title), sub ? el('small', {}, sub) : '');
  void a.offsetWidth;
  a.className = `show ${cls}`;
  clearTimeout(announceTimer);
  announceTimer = setTimeout(() => (a.className = ''), 2700);
}

function loadoutSlots(s) {
  const slots = [];
  for (const w of s.player.weapons) {
    const name = w.evolved ? tx.evoName(w.id) : tx.weaponName(w.id);
    slots.push(
      el('div', { class: `slot${w.evolved ? ' evo' : ''}`, title: name }, [
        iconCopy(w.id, w.evolved ? '#ffd23d' : WEAPONS[w.id].color, 60),
        el('b', {}, w.evolved ? '★' : String(w.level))
      ])
    );
  }
  for (const p of s.player.passives) {
    slots.push(el('div', { class: 'slot passive', title: tx.passiveName(p.id) }, [iconCopy(p.id, '#ffc94d', 60), el('b', {}, String(p.level))]));
  }
  return slots;
}

const hudCache = {};
function setText(id, v) {
  if (hudCache[id] === v) return;
  hudCache[id] = v;
  document.getElementById(id).textContent = v;
}
function setWidth(id, pct) {
  const v = `${Math.max(0, Math.min(100, pct)).toFixed(1)}%`;
  if (hudCache[id] === v) return;
  hudCache[id] = v;
  document.getElementById(id).style.width = v;
}

function updateHud(s) {
  const p = s.player;
  setWidth('xpFill', (p.xp / p.xpNext) * 100);
  setText('levelTag', t('hud.level', { n: p.level }));
  setText('hpText', `${Math.ceil(p.hp)} / ${Math.round(p.stats.maxHp)}`);
  setWidth('hpFill', (p.hp / p.stats.maxHp) * 100);
  setWidth('hpGhost', (p.hp / p.stats.maxHp) * 100);
  setText('timer', fmtTime(Math.floor(s.tick / 60)));
  setText('killCount', fmtNum(s.kills));
  setText('shardCount', fmtNum(s.shards));
  const boss = s.boss && !s.boss.dead ? s.boss : null;
  $('#bossBar').classList.toggle('hidden', !boss);
  if (boss) setText('bossName', boss.def.final ? t('hud.final') : t('hud.boss'));
  if (boss) setWidth('bossFill', (boss.hp / boss.maxHp) * 100);
  const combo = $('#combo');
  if (s.combo >= 10) {
    combo.classList.remove('hidden');
    if (hudCache.combo !== s.combo) {
      hudCache.combo = s.combo;
      combo.firstChild.textContent = `x${s.combo}`;
      combo.classList.remove('bump');
      void combo.offsetWidth;
      combo.classList.add('bump');
    }
  } else if (!combo.classList.contains('hidden')) {
    combo.classList.add('hidden');
  }
  const sig = [...p.weapons.map((w) => w.id + w.level + (w.evolved ? '*' : '')), ...p.passives.map((x) => x.id + x.level)].join();
  if (sig !== game.hudSig) {
    game.hudSig = sig;
    $('#loadout').replaceChildren(...loadoutSlots(s));
  }
}

function onSimEvent(ev, s, live) {
  renderer.handleEvent(ev, s);
  if (!live) return;
  audio.event(ev);
  switch (ev.t) {
    case 'warning':
      if (ev.kind === 'boss') announce(t('ann.boss'), t('ann.bossSub'), 'danger');
      else if (ev.kind === 'final') announce(t('ann.final'), t('ann.finalSub'), 'danger');
      else if (ev.kind === 'meteors') announce(t('ann.meteors'), t('ann.meteorsSub'), 'danger');
      else if (ev.kind === 'elites') announce(t('ann.elites'), t('ann.elitesSub'), 'danger');
      else announce(t('ann.swarm'), t('ann.swarmSub'), 'danger');
      break;
    case 'bossDead':
      portal.happytime();
      announce(t('ann.bossDead'), t('ann.bossDeadSub'), 'good');
      break;
    case 'evolve':
      announce(tx.evoName(ev.id).toUpperCase(), t('ann.evolved'), 'good');
      break;
    case 'hurt': {
      const f = $('#hurtFlash');
      f.classList.add('on');
      setTimeout(() => f.classList.remove('on'), 60);
      if (navigator.vibrate) navigator.vibrate(30);
      break;
    }
    default:
  }
}

// --------------------------------------------------------------------- bucle principal

let last = performance.now();

// Calidad automática: si el equipo no llega a ~45 cuadros por segundo de forma sostenida, se baja
// un nivel (menos resolución interna y menos efectos). Si bajar no mejoró nada (por ejemplo, un
// celular en ahorro de energía que limita a 30 fps), se deja de ajustar en esta sesión.
// El nivel queda guardado y la próxima vez se arranca uno más arriba, por si fue algo pasajero.
const GFX_KEY = 'riftfall.gfx';
const gov = { slow: 0, avg: 1 / 60, check: null, locked: false };
{
  const coarse = window.matchMedia?.('(pointer: coarse)').matches;
  const base = coarse ? 1 : 0;
  let saved = NaN;
  try {
    saved = Number(localStorage.getItem(GFX_KEY));
  } catch {
    /* sin almacenamiento */
  }
  renderer.setLevel(Number.isInteger(saved) && saved > base ? saved - 1 : base);
}

function governor(dt, now) {
  if (gov.locked || document.hidden || dt >= 0.1) return;
  gov.avg = gov.avg * 0.95 + dt * 0.05;
  if (gov.check && now >= gov.check.at) {
    if (gov.avg > gov.check.before * 0.9) gov.locked = true;
    gov.check = null;
    saveGfx();
    return;
  }
  if (gov.check) return;
  gov.slow = dt > 0.0225 ? gov.slow + dt : Math.max(0, gov.slow - dt * 0.5);
  const level = renderer.R.level;
  if (gov.slow > 1.2 && level < QUALITY_LEVELS.length - 1) {
    gov.check = { at: now + 2500, before: gov.avg };
    gov.slow = 0;
    renderer.setLevel(level + 1);
  }
}

function saveGfx() {
  try {
    localStorage.setItem(GFX_KEY, String(renderer.R.level));
  } catch {
    /* sin almacenamiento */
  }
}

function frame(now) {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  governor(dt, now);

  const s = game.sim;
  if (game.mode === 'play' && !game.paused) {
    if (s.phase === 'running') {
      game.acc += dt;
      let steps = 0;
      while (game.acc >= DT && s.phase === 'running' && steps < 8) {
        const d = game.autopilot ? botInput(s) : input.dir();
        game.rec.push(d);
        stepSim(s, d);
        game.acc -= DT;
        steps++;
      }
      if (s.phase !== 'running') game.acc = 0;
    }
    for (const ev of s.events) onSimEvent(ev, s, true);
    s.events.length = 0;
    if (s.phase === 'choice' && !game.choiceOpen) openChoice();
    if (s.phase === 'choice' && game.autopilot && game.choiceOpen) pickChoice(botChoice(s));
    if (s.phase === 'dead' && PORTAL && portal.ads && !game.reviveOffered && s.tick < MAX_TICKS) offerRevive();
    else if (s.phase === 'dead' || s.phase === 'victory') endRun();
    audio.setIntensity(s.boss && !s.boss.dead ? 1 : 0.5 + Math.min(0.45, s.tick / FINAL_TICK));
    updateHud(s);
  } else if (game.mode === 'demo') {
    game.acc += dt;
    while (game.acc >= DT) {
      if (s.phase === 'choice') chooseUpgrade(s, botChoice(s));
      if (s.phase !== 'running') {
        newDemo();
        break;
      }
      stepSim(s, botInput(s));
      game.acc -= DT;
    }
    for (const ev of game.sim.events) onSimEvent(ev, game.sim, false);
    game.sim.events.length = 0;
  } else if (game.mode === 'over') {
    for (const ev of s.events) onSimEvent(ev, s, true);
    s.events.length = 0;
  }

  const alpha = game.mode === 'play' && s.phase === 'running' ? game.acc / DT : 1;
  renderer.render(game.sim, alpha, game.paused ? 0 : dt);

  if (game.mode !== 'play') {
    previewT += dt;
    if (!$('#menu').classList.contains('hidden')) {
      drawShipPreview($('#shipPreview'), app.ship.key, SHIPS[app.ship.key].color, previewT, renderer.R.skin, pilot().loadout);
    }
  }
  requestAnimationFrame(frame);
}

// --------------------------------------------------------------------- eventos de UI

$('#playBtn').addEventListener('click', () => startRun('normal'));
$('#againBtn').addEventListener('click', () => startRun(['arena', 'daily', 'duel'].includes(game.run?.mode) ? game.run.mode : 'normal'));
$('#menuBtn').addEventListener('click', backToMenu);
$('#goTalentsBtn').addEventListener('click', () => {
  backToMenu();
  panels.open('profile');
});
$('#shareBtn').addEventListener('click', async () => {
  if (!lastResult) return;
  const b = $('#shareBtn');
  b.disabled = true;
  try {
    await shareResult(lastResult);
  } finally {
    b.disabled = false;
  }
});
$('#pauseBtn').addEventListener('click', () => setPaused(true));
$('#resumeBtn').addEventListener('click', () => setPaused(false));
$('#quitBtn').addEventListener('click', () => {
  setPaused(false);
  $('#levelup').classList.add('hidden');
  game.choiceOpen = false;
  endRun();
});
$('#walletBtn').addEventListener('click', async () => {
  audio.unlock();
  if (app.wallet?.connected) panels.open(app.config?.staticMode ? 'hangar' : 'profile');
  else await connectWallet();
});
$('#muteBtn').addEventListener('click', () => {
  audio.unlock();
  $('#muteBtn').classList.toggle('off', audio.toggleMute());
});
$('#muteBtn').classList.toggle('off', audio.muted);
document.querySelectorAll('[data-open]').forEach((b) =>
  b.addEventListener('click', () => {
    audio.unlock();
    audio.play('click');
    panels.open(b.dataset.open);
  })
);
// El navegador solo deja sonar el audio después de un toque o una tecla (la primera partida del
// portal arranca sola, sin ese toque).
for (const ev of ['pointerdown', 'keydown']) window.addEventListener(ev, () => audio.unlock(), { once: true });
window.addEventListener('keydown', (e) => {
  if (game.mode === 'play') {
    if (game.choiceOpen && ['Digit1', 'Digit2', 'Digit3', 'Numpad1', 'Numpad2', 'Numpad3'].includes(e.code)) {
      pickChoice(Number(e.code.slice(-1)) - 1);
    } else if (e.code === 'Escape' || e.code === 'KeyP') {
      setPaused(!game.paused);
    }
  } else if (e.code === 'Escape') {
    panels.close();
  } else if (e.code === 'Enter' && game.mode === 'demo' && $('#sheet').classList.contains('hidden')) {
    startRun('normal');
  }
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden && game.mode === 'play' && !game.choiceOpen) setPaused(true);
});
$('#controlsHint').textContent = matchMedia('(pointer: coarse)').matches ? t('menu.hintTouch') : t('menu.hintKeys');
const langSel = $('#langSel');
for (const [code, name] of Object.entries(LANGS)) langSel.append(el('option', { value: code, selected: code === lang }, name));
langSel.addEventListener('change', () => setLang(langSel.value));

// --------------------------------------------------------------------- arranque

/** Visitas con Vercel Web Analytics (sin cookies ni datos personales), solo en la web publicada. */
function setupAnalytics() {
  if (!/(^|\.)duckdns\.org$|\.vercel\.app$/.test(location.hostname)) return;
  window.va ??= (...args) => (window.vaq ??= []).push(args);
  document.head.append(el('script', { defer: true, src: '/_vercel/insights/script.js' }));
}

async function boot() {
  if (PORTAL) {
    // Portales: sin servidor, sin wallet ni pagos; solo el juego, en modo práctica.
    await initPortal();
    portal.onMute((m) => audio.setForcedMute(m));
    // El progreso puede venir de la cuenta del portal (otro dispositivo).
    app.progress = loadProgress();
    readRiftChoice();
    portal.loadingStart();
    $('.tagline').innerHTML = t('menu.taglinePortal');
    newDemo();
    requestAnimationFrame(frame);
    updateMenu();
    setNet(t('net.practice'), 'warn');
    portal.loadingStop();
    // Quien nunca jugó entra directo a la partida (regla del portal: nada de menús antes de jugar).
    if (!app.progress.runs) startRun('normal');
    return;
  }
  // Progreso que llega en el link (por ejemplo, al abrir el juego dentro de MetaMask o al mudarse).
  const moved = await receiveMove({ applyRiftfall: (d) => applyTransfer(d), applyCargo: applyCargoTransfer });
  const received = await receiveTransfer();
  // Aplicar primero el progreso entrante permite volver a empaquetarlo sin secretos al mudarse.
  if (await moveIfOldHost()) return;
  setupPwa();
  setupAnalytics();
  if (received || moved) {
    app.progress = loadProgress();
    app.ship = loadShipChoice();
    readRiftChoice();
    setTimeout(() => toast(t('toast.transferred'), 'ok'), 400);
  }
  readIncomingDuel();
  // Desde Rift Cargo se puede llegar directo al Pase Fundador (/?panel=founder).
  if (!PORTAL && new URLSearchParams(location.search).get('panel') === 'founder') setTimeout(() => panels.open('founder'), 300);
  newDemo();
  requestAnimationFrame(frame);
  applyCosmetics();
  claimBridge();
  addEventListener('visibilitychange', () => document.visibilityState === 'visible' && claimBridge());
  updateMenu();
  try {
    app.config = await api.config();
    app.profile = await api.session();
    app.online = true;
    if (app.config.chain) {
      brandText($('#menu'), app.config.chain.tokenSymbol);
      app.wallet = createWallet(app.config.chain);
      // Al pagar el Pase Fundador la wallet cambia a la red principal: eso no debe recargar la página.
      app.wallet.onChange(() => founderBusy() || location.reload());
      setNet(t('net.online', { net: app.config.chain.network, id: app.config.chain.chainId }), 'ok');
    } else {
      setNet(t('net.onlineNoChain'), 'warn');
    }
    if (app.ship.tokenId && !app.config.chain) selectShip({ key: 'spark', tokenId: null, level: 1 });
  } catch {
    // Sin servidor del juego: si junto al juego está publicada la configuración del token
    // (deployment.json, generada por el Lanzador), la tienda de naves, la forja y el mercado
    // funcionan igual directamente con la wallet.
    const dep = await fetch('/deployment.json')
      .then((r) => (r.ok && (r.headers.get('content-type') ?? '').includes('json') ? r.json() : null))
      .catch(() => null);
    if (dep?.contracts?.RiftShips) {
      app.config = { chain: chainConfigFromDeployment(dep), staticMode: true, riftPerShard: 1, minClaimShards: 100, demoShips: false, missions: [] };
      brandText($('#menu'), app.config.chain.tokenSymbol);
      app.wallet = createWallet(app.config.chain);
      // Al pagar el Pase Fundador la wallet cambia a la red principal: eso no debe recargar la página.
      app.wallet.onChange(() => founderBusy() || location.reload());
      setNet(TESTNETS.includes(app.config.chain.chainId) ? t('net.shopTest') : t('net.shop'), 'warn');
      // Si la wallet ya estaba conectada, sigue conectada al volver o recargar (sin abrir MetaMask).
      app.wallet
        .reconnect()
        .then(async (address) => {
          if (!address) return;
          updateMenu();
          app.balances = await app.wallet.balances().catch(() => null);
          updateMenu();
        })
        .catch(() => {});
    } else {
      setNet(t('net.practice'), 'warn');
    }
  }
  // El ranking mundial es de la web publicada (función /api/daily); con servidor propio el desafío usa el suyo.
  // La Cuenta Rift también es de la web publicada (con servidor propio, la cuenta es la de ese servidor).
  if (!app.online) {
    setupAccount();
    refreshWorld();
    refreshRunBoard();
  }
  updateMenu();
}

/** Ruta Rift: lo que creció tu empresa en Rift Cargo da Núcleos acá (una vez por escalón). */
function claimBridge() {
  const got = bridgePending(TO_RIFTFALL, app.progress.bridge ?? 0, cargoState());
  // El link a Rift Cargo muestra el próximo premio.
  const nx = bridgeNext(TO_RIFTFALL, (app.progress.bridge ?? 0) + got.length);
  const link = document.querySelector('.cargo-link');
  if (link) link.dataset.hint = nx ? t('bridge.next', { lv: nx.level, n: fmtNum(nx.cores) }) : '';
  if (!got.length) return;
  const n = got.reduce((a, g) => a + g.cores, 0);
  app.progress.cores += n;
  app.progress.bridge = got[got.length - 1].index + 1;
  saveProgress(app.progress);
  setTimeout(() => toast(t('bridge.got', { n: fmtNum(n) }), 'ok'), 600);
  updateMenu();
}

// --------------------------------------------------------------------- Cuenta Rift

/** La cuenta guarda el progreso en la nube y lo trae en cualquier dispositivo (también en Rift Cargo). */
let accountUI = null;
function setupAccount() {
  if (PORTAL) return;
  // Panel del dueño: cada herramienta cambia el progreso de este dispositivo (y la cuenta lo sube a la nube).
  const boost = (kind) => () => {
    app.progress = ownerBoost(loadProgress(), kind);
    updateRiftPick();
    updateMenu();
  };
  accountUI = createAccountUI({
    game: 'riftfall',
    lang: () => lang,
    toast,
    onChange: () => {
      applyCosmetics();
      updateMenu();
      attachRemoteWallet();
    },
    owner: { cores: boost('cores'), talents: boost('talents'), rift: boost('rift'), parts: boost('parts') },
    // En el celular sin wallet: el juego se abre en MetaMask con tu cuenta y tu progreso.
    walletLink: () => {
      saveProgress(app.progress);
      return metamaskLink();
    }
  });
  $('#accountBtn').addEventListener('click', () => accountUI.open());
  // Pago en pesos: solo aparece si el servidor lo tiene configurado.
  configureFiat({
    lang: () => lang,
    toast,
    openAccount: () => accountUI.open(),
    // Se acreditó con la ventana abierta: el Pase ya está en este dispositivo.
    onPaid: () => {
      applyCosmetics();
      updateMenu();
    }
  });
  loadFiat();
  const sync = createSync('riftfall', {
    get: () => loadProgress(),
    merge: mergeProgress,
    apply: (p) => {
      saveProgress(p);
      app.progress = loadProgress();
      updateMenu();
    }
  });
  addEventListener('riftfall:progress', () => sync.schedule());
  const flush = () => sync.flush();
  addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flush());
  onAccount(async (a) => {
    renderAccountBtn();
    // El ranking marca tu fila con el id de la cuenta.
    worldMe = await myPublicId();
    if (a?.player?.name) try { localStorage.setItem(NAME_KEY, a.player.name); } catch { /* sin almacenamiento */ }
    renderRunBoard();
  });
  renderAccountBtn();
  startAccount().then(async (a) => {
    if (!a) return;
    await sync.pull();
    // Compras en la cuenta (Pase Fundador, estéticos) ↔ las de este dispositivo.
    if (await syncPurchases()) applyCosmetics();
    updateMenu();
  });
}

function renderAccountBtn() {
  const btn = $('#accountBtn');
  if (!btn) return;
  const a = rgAccount();
  // Sin servidor de cuentas (por ejemplo, un sitio sin la nube) no se muestra.
  btn.classList.toggle('hidden', PORTAL || !a);
  btn.classList.toggle('guest', !a || a.player.guest);
  btn.querySelector('b').textContent = a?.player?.name || (a ? t('acct.guest') : t('acct.title'));
}

boot();

// Gancho para pruebas automatizadas y capturas (piloto automático y avance rápido).
// Solo existe en desarrollo y en el build de tests (`npm run build:e2e`): en producción
// no se expone, para no regalar un bot de farmeo a un clic de la consola.
if (import.meta.env.DEV || import.meta.env.MODE === 'e2e' || import.meta.env.VITE_E2E_HOOK === '1') window.__RIFTFALL__ = {
  game,
  app,
  audio,
  renderer,
  drawShipPreview,
  gov,
  startRun,
  pickChoice,
  setPaused,
  setAutopilot(v) {
    game.autopilot = v;
  },
  fastForward(seconds) {
    const s = game.sim;
    const end = s.tick + seconds * 60;
    // Resuelve también la mejora abierta justo en el último tick del avance.
    while ((s.tick < end || s.phase === 'choice') && (s.phase === 'running' || s.phase === 'choice')) {
      if (s.phase === 'choice') {
        const c = botChoice(s);
        game.rec.choice(c);
        chooseUpgrade(s, c);
        game.choiceOpen = false;
        continue;
      }
      const d = botInput(s);
      game.rec.push(d);
      stepSim(s, d);
      s.events.length = 0;
    }
    $('#levelup').classList.add('hidden');
  }
};
